
<script setup>

  import { ref } from 'vue';

  import { onBeforeRouteLeave } from 'vue-router';

  import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';

  import { ChevronUpIcon, PlusCircleIcon, ArrowRightCircleIcon } from '@heroicons/vue/24/outline';

  import ImageSequence from '../components/image-sequence.vue';

  import FitContainer from '../components/fit-container.vue';

  import SimpleDialog from '../dialogs/simple.vue';

  import CarIcon from '../icons/car-icon.vue';

  import backgroundPath from '../images/road-background.jpg';

  import partials from '../partials/activity-2-part-1-*.vue';

  import sequencePaths from '../sequences/activity-2-car-*.png';

  const sequence = Object.values( sequencePaths );

  const frame = ref( 0 );

  const mediumSize = matchMedia( '( min-width: 768px )' );

  const hotspots = [

    { id: 'gas', top: 42, left: 37.5, title: 'Extra Gas' },

    { id: 'tools', top: 50, left: 26.5, title: 'Tools and Repair Kit' },

    { id: 'book', top: 57, left: 36.5, title: 'Green Book' },

    { id: 'blankets', top: 54, left: 55, title: 'Blankets and Pillows' },

    { id: 'coffee', top: 47, left: 66.5, title: 'Coffee Can' },

    { id: 'food', top: 52, left: 74, title: 'Extra Food and Water' }

  ].map( obj => ( { ...obj, dialog: ref( null ) } ) );

  function setDialog( id, el ) {

    const hotspot = hotspots.find( hotspot => hotspot.id === id );

    hotspot.dialog.value = el;

  }

  function animate( to ) {

    return new Promise( resolve => {

      let start = null;

      const from = frame.value;

      const distance = Math.abs( to - from );

      const direction = Math.sign( to - from );

      function step( now ) {

        if ( ! start ) start = now;

        const progress = Math.min( Math.max( ( now - start ) / 1000, 0 ), 1 );

        frame.value = Math.round( from + distance * direction * progress );

        if ( progress < 1 ) {
          
          requestAnimationFrame( step );

        }

        else {
          
          frame.value = to;

          resolve();

        }

      }

      if ( from !== to ) requestAnimationFrame( step );

    } );

  }

  onBeforeRouteLeave( async to => {

    if ( mediumSize.matches && to.path === '/activity/2/part/2' )
    
      await animate( sequence.length - 1 );

  } );

</script>

<template>

  <article>

    <div class="grid">

      <div class="sticky top-header row-start-1 col-start-1 w-full h-view overflow-hidden">

        <div class="w-full h-full bg-fixed bg-cover bg-center scale-110"
        
          :style="{ backgroundImage: `radial-gradient( rgba( 24, 22, 21, 0.6 ) 40%, rgba( 24, 22, 21, 1 ) ), url( ${ backgroundPath } )` }" />

      </div>

      <div class="md:hidden row-start-1 col-start-1 relative flex items-center justify-center min-h-view px-6 py-12">

        <div class="w-full max-w-xl drop-shadow-[0_0_5px_rgba(0,0,0,0.5)]">

          <h3 class="text-5xl font-heading text-white uppercase">See What You Brought On The Trip</h3>

          <p class="text-white mb-10">Click an item to learn more.</p>

          <div v-for="{ id, title } in hotspots" class="mt-4 bg-white">

            <Disclosure v-slot="{ open }">

              <DisclosureButton class="group flex w-full items-center justify-between p-6">

                <div class="text-xl group-hover:underline group-focus-visible:underline">
                  
                  {{ title }}
                
                </div>

                <ChevronUpIcon :class="[ 'h-6 w-6', { 'rotate-180 transform': ! open } ]" />

              </DisclosureButton>

              <DisclosurePanel class="px-6 pt-3 pb-9">

                <component :is="partials[ id ].default" />

              </DisclosurePanel>

            </Disclosure>

          </div>

          <RouterLink
          
            class="inline-flex items-center gap-4 px-6 py-4 mt-12 font-bold text-white bg-green-700 transition-colors pointer-events-auto
            
              hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline"
          
            to="/activity/2/part/2">

            <span>Continue</span>

            <ArrowRightCircleIcon class="w-8" />

          </RouterLink>

        </div>

      </div>

      <div class="hidden md:block row-start-1 col-start-1 w-full h-view">

        <ImageSequence width="1440" height="1440" :sequence="sequence" :frame="frame" class="relative w-full h-full object-cover" />

      </div>

      <div :class="[ 'hidden md:flex row-start-1 col-start-1 justify-center items-start w-full h-view px-5 py-16 transition-opacity duration-200',
      
        frame > 0 ? 'opacity-0' : 'opacity-100' ]">

        <div class="flex gap-10 text-white drop-shadow-[0_0_5px_rgba(0,0,0,1)]">

          <CarIcon class="w-12" />

          <div>

            <h3 class="text-5xl font-heading uppercase">See What They Brought on a Trip</h3>

            <p>Click an item to learn more.</p>

          </div>

        </div>

      </div>

      <FitContainer :class="[ 'hidden md:block row-start-1 col-start-1 w-full h-view transition-opacity duration-200', frame > 0 ? 'opacity-0' : 'opacity-100' ]"
        
        :width="1440" :height="1440" fit="cover">

        <button v-for="{ top, left, title, dialog } in hotspots" type="button" @click="dialog.value.open()"
        
          class="group absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2" :style="{ top: `${ top }%`, left: `${ left }%` }">

          <PlusCircleIcon class="w-10 h-10 text-amber-300" />

          <div class="absolute top-1/2 right-0 w-32 translate-x-full -translate-y-1/2 pl-2
          
            leading-tight text-left text-white text-lg drop-shadow-[0_0_5px_rgba(0,0,0,1)]
          
            group-hover:underline group-focus-visible:underline">
            
            {{ title }}
          
          </div>

        </button>

      </FitContainer>

      <div :class="[
        
        'hidden md:flex row-start-1 col-start-1 relative justify-center items-end w-full h-view px-5 py-16 transition-opacity duration-200 pointer-events-none',
      
        frame > 0 ? 'opacity-0' : 'opacity-100'
        
      ]">
      
        <RouterLink
        
          class="flex items-center gap-4 px-6 py-4 font-bold text-white bg-green-700 transition-colors pointer-events-auto
          
            hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline"
        
          to="/activity/2/part/2">

          <span>Continue</span>

          <ArrowRightCircleIcon class="w-8" />

        </RouterLink>

      </div>

    </div>

    <SimpleDialog v-for="{ id, title } in hotspots" :ref="el => setDialog( id, el )" :title="title" class="max-w-xl">
    
      <component :is="partials[ id ].default" />

    </SimpleDialog>

  </article>

</template>
